import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, switchMap, take, tap, throwError } from 'rxjs';
import { BaseDomainModel } from '../../../models/base/base-domain-model';
import { MediaManagerAssetFolderDto } from '../../../models/image/dto/media-manager-asset-folder-dto';
import { MediaManagerFolderDto } from '../../../models/image/dto/media-manager-folder-dto';
import { MediaManagerLibraryContext, MediaManagerStore } from '../store';
import { applyCreatedFolder, applyDeletedFolder, applyUpdatedFolder, findFolderInLibrary } from '../mutations';
import {
  preflightCreateFolderValidation,
  preflightDeleteFolderValidation,
  preflightFolderUpdateValidation
} from '../validation';
import { AssetRepository } from '../../../repositories/asset/asset-repository';

/**
 * REST-based folder mutations: create, rename/move, and delete. Every mutation follows the same
 * preflight -> API call -> in-memory patch -> fallback-to-refetch pattern.
 *
 * Folder deletes take a short detour through the store's `locallyDeletingFolderIds$` state so the
 * folder sync service knows to skip its own reconciliation of the resulting soft-delete WS event - the
 * local delete has already patched state.
 *
 * Only folder-level mutations live here. Bulk-delete-across-folders lives in
 * MediaManagerBulkDeleteService; per-folder-content-move lives in the mutations module.
 */
@Injectable() // Provided by Logged In Scope
export class MediaManagerFolderService extends BaseDomainModel {
  constructor(
    private assetRepository: AssetRepository,
    private store: MediaManagerStore
  ) {
    super();
  }

  createAssetFolder$(req: MediaManagerFolderDto): Observable<MediaManagerAssetFolderDto> {
    return this.store.getActiveLibraryContext$().pipe(
      switchMap(context => {
        const { filesystem, assetLibrary } = context;
        const preflightErrors = preflightCreateFolderValidation(req, filesystem, assetLibrary);
        if (preflightErrors) {
          return throwError(() => new Error(preflightErrors));
        }
        return this.createAssetFolderWithValidatedState$(req, context);
      })
    );
  }

  private createAssetFolderWithValidatedState$(
    req: MediaManagerFolderDto,
    context: MediaManagerLibraryContext
  ): Observable<MediaManagerAssetFolderDto> {
    const { brandId, filesystem, assetLibrary } = context;
    return this.assetRepository.createAssetFolder$(req, brandId).pipe(
      switchMap(createdFolderResponse => {
        const inMemoryPatchSucceeded = applyCreatedFolder(createdFolderResponse.folder, filesystem, assetLibrary);
        if (!inMemoryPatchSucceeded) {
          return this.store.refreshBrandAssetLibrary$(brandId).pipe(
            switchMap(() => {
              return throwError(() => {
                return new Error(
                  'Incremental patch failed to create folder. AssetLibrary fetched and rebuilt Filesystem.'
                );
              });
            })
          );
        }
        this.store.notifyFilesystemChanged('asset folder created');
        return of(createdFolderResponse);
      })
    );
  }

  deleteAssetFolder$(
    id: string,
    targetFolderId: string,
    deleteAllItems: boolean
  ): Observable<MediaManagerAssetFolderDto> {
    return this.store.getActiveLibraryContext$().pipe(
      switchMap(context => {
        const { filesystem, assetLibrary } = context;
        const preflightErrors = preflightDeleteFolderValidation(
          id,
          targetFolderId,
          deleteAllItems,
          filesystem,
          assetLibrary
        );
        if (preflightErrors) {
          return throwError(() => new Error(preflightErrors));
        }
        return this.markFolderDeleteInProgress$(id).pipe(
          switchMap(() => {
            return this.deleteAssetFolderWithValidatedState$(id, targetFolderId, deleteAllItems, context);
          }),
          switchMap(deletedFolderResponse => {
            return this.clearFolderDeleteInProgress$(id).pipe(map(() => deletedFolderResponse));
          }),
          catchError(err => {
            return this.clearFolderDeleteInProgress$(id).pipe(switchMap(() => throwError(() => err)));
          })
        );
      })
    );
  }

  /**
   * Track this folder id in `locallyDeletingFolderIds$` so the folder sync service will skip reconciling
   * the WS event that will eventually announce this delete - we already patched state locally.
   */
  private markFolderDeleteInProgress$(folderId: string): Observable<void> {
    if (!folderId?.trim()) return of(undefined);
    return this.store.locallyDeletingFolderIds$.pipe(
      take(1),
      tap(folderIds => {
        const nextFolderIds = new Set(folderIds);
        nextFolderIds.add(folderId);
        this.store.connectToLocallyDeletingFolderIds(nextFolderIds);
      }),
      map((): void => undefined)
    );
  }

  private clearFolderDeleteInProgress$(folderId: string): Observable<void> {
    if (!folderId?.trim()) return of(undefined);
    return this.store.locallyDeletingFolderIds$.pipe(
      take(1),
      tap(folderIds => {
        const nextFolderIds = new Set(folderIds);
        nextFolderIds.delete(folderId);
        this.store.connectToLocallyDeletingFolderIds(nextFolderIds);
      }),
      map((): void => undefined)
    );
  }

  private deleteAssetFolderWithValidatedState$(
    id: string,
    targetFolderId: string,
    deleteAllItems: boolean,
    context: MediaManagerLibraryContext
  ): Observable<MediaManagerAssetFolderDto> {
    const { brandId, filesystem, assetLibrary } = context;
    return this.assetRepository.deleteAssetFolder$(id, targetFolderId, deleteAllItems, brandId).pipe(
      switchMap(deletedFolderResponse => {
        const inMemoryPatchSucceeded = applyDeletedFolder(
          id,
          targetFolderId,
          deleteAllItems,
          deletedFolderResponse,
          filesystem,
          assetLibrary
        );
        if (!inMemoryPatchSucceeded) {
          return this.store.refreshBrandAssetLibrary$(brandId).pipe(
            switchMap(() => {
              return throwError(() => {
                return new Error(
                  'Incremental patch failed to delete folder. AssetLibrary fetched and rebuilt Filesystem.'
                );
              });
            })
          );
        }
        this.store.notifyFilesystemChanged('asset folder deleted');
        return of(deletedFolderResponse);
      })
    );
  }

  updateAssetFolder$(req: MediaManagerFolderDto): Observable<MediaManagerFolderDto> {
    return this.store.getActiveLibraryContext$().pipe(
      switchMap(context => {
        const { filesystem, assetLibrary } = context;
        const canonicalSelectedFolder = findFolderInLibrary(req.id, assetLibrary);
        if (!canonicalSelectedFolder) {
          return throwError(() => new Error(`MediaManager folder '${req.id}' could not be found in assetLibrary`));
        }
        const preflightErrors = preflightFolderUpdateValidation(req, canonicalSelectedFolder, filesystem, assetLibrary);
        if (preflightErrors) {
          return throwError(() => new Error(preflightErrors));
        }
        return this.updateAssetFolderWithValidatedState$(req, canonicalSelectedFolder, context);
      })
    );
  }

  private updateAssetFolderWithValidatedState$(
    req: MediaManagerFolderDto,
    canonicalSelectedFolder: MediaManagerFolderDto,
    context: MediaManagerLibraryContext
  ): Observable<MediaManagerFolderDto> {
    const { brandId, filesystem } = context;
    // TODO: Could use the entire updatedFolder: MediaManagerAssetFolderDto to assist with debugging in the
    // event that folder moving/metadata patching is causing fallback assetLibrary re-fetches and rebuilds.
    return this.assetRepository.updateAssetFolder$(req, brandId).pipe(
      switchMap(updatedFolder => {
        const inMemoryPatchSucceeded = applyUpdatedFolder(canonicalSelectedFolder, updatedFolder.folder, filesystem);
        if (!inMemoryPatchSucceeded) {
          return this.store.refreshBrandAssetLibrary$(brandId).pipe(
            switchMap(() => {
              return throwError(() => {
                return new Error(
                  'Incremental patch failed to update folder. AssetLibrary fetched and rebuilt Filesystem.'
                );
              });
            })
          );
        }
        this.store.notifyFilesystemChanged('asset folder updated');
        return of(updatedFolder.folder);
      })
    );
  }
}
